import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import categoryApi from '../../api/categoryApi';

/* ------------------------------------------------------------------ */
/* Async Thunks */
/* ------------------------------------------------------------------ */

export const fetchCategories = createAsyncThunk(
  'category/fetchAll',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const res = await categoryApi.getAll(page, pageSize, search);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || 'Failed to load categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'category/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await categoryApi.create(data);
      return res;
    } catch (e) {
      const error =
        // e.response?.data?.is_canteen?.[0] ||
        e.response?.data?.is_it?.[0] ||
        'Something went wrong';

      return rejectWithValue(error);
    }
  }
);


export const updateCategory = createAsyncThunk(
  'category/update',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await categoryApi.update(id, data);
      return res;
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'category/delete',
  async (id, { rejectWithValue }) => {
    try {
      await categoryApi.delete(id);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errors?.[0] || 'Delete failed');
    }
  }
);

/* -------------------------- EXPORT -------------------------- */
export const exportCategories = createAsyncThunk(
  'category/exportCategories',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await categoryApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `categories_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

/* -------------------------- IMPORT PREVIEW -------------------------- */
export const importPreview = createAsyncThunk(
  'category/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await categoryApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

/* -------------------------- IMPORT CONFIRM -------------------------- */
export const importConfirm = createAsyncThunk(
  'category/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await categoryApi.importConfirm(preview);
      dispatch(fetchCategories({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

/* ------------------------------------------------------------------ */
/* Slice */
/* ------------------------------------------------------------------ */

const categorySlice = createSlice({
  name: 'category',
  initialState: {
    categories: [],
    pagination: { count: 0, page_size:10, next: null, previous: null },
    isLoading: false,
    error: null,
    importPreview: null,
    importTotal: null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
  },
extraReducers: (builder) => {
  //FETCH
    builder
      .addCase(fetchCategories.pending, (s) => { s.isLoading = true; })
      .addCase(fetchCategories.fulfilled, (s, a) => {
        s.isLoading = false;
        s.categories = a.payload.data || [];
        s.pagination = a.payload.pagination || { count: 0, page_size: 10 };
      })
      .addCase(fetchCategories.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // CREATE
    builder.addCase(createCategory.fulfilled, (s, a) => {
      s.categories.unshift(a.payload);
      s.pagination.count += 1;
    });

    // UPDATE
    builder.addCase(updateCategory.fulfilled, (s, a) => {
      const idx = s.categories.findIndex(c => c.id === a.payload.id);
      if (idx !== -1) s.categories[idx] = a.payload;
    });

    // DELETE
    builder.addCase(deleteCategory.fulfilled, (s, a) => {
      s.categories = s.categories.filter(c => c.id !== a.payload);
      s.pagination.count -= 1;
    });

    // EXPORT
    builder.addCase(exportCategories.rejected, (s, a) => {
      s.error = a.payload;
    });

    // IMPORT PREVIEW
    builder
      .addCase(importPreview.pending, (s) => { s.isLoading = true; })
      .addCase(importPreview.fulfilled, (s, a) => {
        s.isLoading = false;
        s.importPreview = a.payload.preview;
        s.importTotal = a.payload.total;
      })
      .addCase(importPreview.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // IMPORT CONFIRM
    builder
      .addCase(importConfirm.pending, (s) => { s.isLoading = true; })
      .addCase(importConfirm.fulfilled, (s) => {
        s.isLoading = false;
        s.importPreview = null;
        s.importTotal = null;
      })
      .addCase(importConfirm.rejected, (s, a) => {
        s.isLoading = false;
        s.error = a.payload;
      });

    // Shared pending/rejected
    ['createCategory', 'updateCategory', 'deleteCategory'].forEach(action => {
      builder
        .addCase(action + '.pending', (s) => { s.isLoading = true; })
        .addCase(action + '.rejected', (s, a) => {
          s.isLoading = false;
          s.error = a.payload;
        });
    });
  },
});

export const { clearError } = categorySlice.actions;
export default categorySlice.reducer;